import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { BaseWorkSafeBcSubSection, IBaseWorkSafeBcSubSectionProps } from "../BaseWorkSafeBcSubSection";
import { WorkSafeBcFormValidator } from "../Models/WorkSafeBcFormValidator";

class WorksSafeBcEmployeeSectionState {
    public maxServiceDate: Date;
    public maxInjuryOrFirstTreatmentForConditionDate: Date;
}

class WorksSafeBcEmployeeSection extends BaseWorkSafeBcSubSection<IBaseWorkSafeBcSubSectionProps, WorksSafeBcEmployeeSectionState> {
    constructor(props: IBaseWorkSafeBcSubSectionProps) {
        super(props);

        this.state = {
            maxServiceDate: DateOnly.today().toJSDate(),
            maxInjuryOrFirstTreatmentForConditionDate: props.model.ServiceDate
        };
    }

    public componentWillReceiveProps(nextProps: IBaseWorkSafeBcSubSectionProps): void {
        const maxServiceDate = DateOnly.today().toJSDate();
        const maxInjuryOrFirstTreatmentForConditionDate = nextProps.model.ServiceDate;

        if (this.state.maxServiceDate !== maxServiceDate
            || this.state.maxInjuryOrFirstTreatmentForConditionDate !== maxInjuryOrFirstTreatmentForConditionDate
        ) {
            this.mutateState(s => {
                s.maxServiceDate = maxServiceDate;
                s.maxInjuryOrFirstTreatmentForConditionDate = maxInjuryOrFirstTreatmentForConditionDate;
            });
        }
    }

    public render(): JSX.Element {
        const data = this.props.model;
        const validator = new WorkSafeBcFormValidator(data);

        const workerCountryId = data.OperatingLocationAddress.CountryLookupId || this.canadaLookUpId;
        const validationRule = this.validationHelper.getPhoneNumberValidationRule(workerCountryId, null);
        const mask = validationRule?.EditMaskPattern ?? "";

        return (
            <div className="flex-grow-container">
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_DateOfServiceLabel",
                        value: DateHelper.formatDate(data.ServiceDate, this.dateFormat),
                        required: true
                    },
                    <DateSelector
                        id={`WorkSafeBc_DateOfService_${this.props.formId}`}
                        onChangeCallback={date => this.props.mutateModelState(s => s.ServiceDate = date)}
                        date={data.ServiceDate}
                        valid={validator.isServiceDateValid()}
                        maxDate={this.state.maxServiceDate}
                        dateFormat={this.dateFormatForDateSelector}
                        width={150}
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_EmployerNameLabel",
                        value: data.EmployerName,
                        required: true
                    },
                    <TextboxControl
                        id={`WorkSafeBc_EmployerName_${this.props.formId}`}
                        onChange={value => this.props.mutateModelState(s => s.EmployerName = value)}
                        value={data.EmployerName}
                        maxLength={100}
                        valid={validator.isEmployerNameValid()}
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_EmployersPhoneNumberLabel",
                        value: this.validationHelper.getFormattedPhoneNumberString(data.EmployerPhoneNumber, workerCountryId)
                    },
                    <TextboxMaskedControl
                        allowEmpty
                        isValid={this.validationHelper.isFieldValid(data.EmployerPhoneNumber, validationRule)}
                        id={"WorkSafeBc_EmployersPhoneNumber" + this.props.formId}
                        value={data.EmployerPhoneNumber}
                        mask={mask}
                        style={{ width: "150px" }}
                        onChangeFunction={telephone => this.props.mutateModelState(s => s.EmployerPhoneNumber = telephone)}
                    />
                )}
                {this.renderAddress(
                    "WorkSafeBc_OperatingLocationAddressLabel",
                    `EmployerAddress_${this.props.formId}`,
                    data.OperatingLocationAddress,
                    model => this.props.mutateModelState(s => s.OperatingLocationAddress = model),
                    false
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_InjuryOrFirstTreatedForConditionLabel",
                        value: DateHelper.formatDate(data.InjuryOrFirstTreatmentForConditionDate, this.dateFormat),
                        required: true
                    },
                    <DateSelector
                        id={`InjuryOrFirstTreatedForConditiom_${this.props.formId}`}
                        onChangeCallback={date => this.props.mutateModelState(s => s.InjuryOrFirstTreatmentForConditionDate = date)}
                        date={data.InjuryOrFirstTreatmentForConditionDate}
                        dateFormat={this.dateFormatForDateSelector}
                        valid={validator.isInjuryOrFirstTreatmentForConditionDateValid()}
                        width={150}
                        maxDate={this.state.maxInjuryOrFirstTreatmentForConditionDate}
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_WhoRenderedFirstTreatmentLabel",
                        value: data.FirstTreatmentRendererName
                    },
                    <TextboxControl
                        id={`WorkSafeBc_FirstTreatmentRendererName_${this.props.formId}`}
                        onChange={value => this.props.mutateModelState(s => s.FirstTreatmentRendererName = value)}
                        value={data.FirstTreatmentRendererName}
                        maxLength={25}
                    />
                )}
            </div>
        );
    }
}

export { WorksSafeBcEmployeeSection };
